(() => {
  const formatMoney = (cents, currency = 'USD', locale = document.documentElement.lang || 'en') => {
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format((Number(cents) || 0) / 100);
    } catch (error) {
      return ((Number(cents) || 0) / 100).toFixed(2) + ' ' + currency;
    }
  };

  const showToast = (message) => {
    const toast = document.querySelector('[data-site-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.hidden = false;
    toast.classList.add('is-visible');
    window.clearTimeout(showToast.timer);
    showToast.timer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => { toast.hidden = true; }, 250);
    }, 3500);
  };

  const refreshCartCount = async () => {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('[data-cart-count]').forEach((count) => {
        count.textContent = cart.item_count;
        count.hidden = cart.item_count === 0;
      });
      const cartLink = document.querySelector('.site-header__cart');
      if (cartLink) cartLink.setAttribute('aria-label', 'Shopping bag, ' + cart.item_count + ' items');
    } catch (error) {
      // The cart page remains available if the Ajax API is unavailable.
    }
  };

  const addItems = async (url, items) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.description || result.message || 'Please try again.');
    await refreshCartCount();
    return result;
  };

  document.addEventListener('click', async (event) => {
    const menuButton = event.target.closest('[data-menu-toggle]');
    if (menuButton) {
      const menu = document.querySelector('[data-mobile-menu]');
      const opened = menuButton.getAttribute('aria-expanded') === 'true';
      menuButton.setAttribute('aria-expanded', String(!opened));
      menuButton.setAttribute('aria-label', opened ? 'Open menu' : 'Close menu');
      if (menu) menu.hidden = opened;
      return;
    }

    const quickAdd = event.target.closest('[data-quick-add]');
    if (quickAdd) {
      if (quickAdd.disabled) return;
      const original = quickAdd.textContent;
      quickAdd.disabled = true;
      quickAdd.textContent = 'Adding…';
      try {
        const root = window.Shopify?.routes?.root || '/';
        await addItems(root + 'cart/add.js', [{ id: Number(quickAdd.dataset.quickAdd), quantity: 1 }]);
        quickAdd.textContent = 'Added';
        showToast('Added to your bag.');
      } catch (error) {
        quickAdd.textContent = original;
        showToast(error.message);
      } finally {
        window.setTimeout(() => {
          quickAdd.disabled = false;
          quickAdd.textContent = original;
        }, 1400);
      }
      return;
    }

    const thumb = event.target.closest('[data-gallery-thumb]');
    if (thumb) {
      const id = thumb.dataset.galleryThumb;
      const gallery = thumb.closest('.product-gallery');
      gallery?.querySelectorAll('[data-gallery-item]').forEach((item) => {
        item.classList.toggle('is-hidden', item.dataset.galleryItem !== id);
      });
      gallery?.querySelectorAll('[data-gallery-thumb]').forEach((button) => {
        button.classList.toggle('is-active', button === thumb);
      });
      return;
    }

    const share = event.target.closest('[data-share]');
    if (share) {
      const shareData = { title: document.title, url: window.location.href };
      if (navigator.share) {
        navigator.share(shareData).catch(() => {});
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(shareData.url).then(() => showToast('Link copied.')).catch(() => {});
      }
    }
  });

  document.addEventListener('change', (event) => {
    const sort = event.target.closest('[data-auto-submit]');
    if (sort) sort.form?.requestSubmit();

    const variant = event.target.closest('[data-product-variant]');
    if (variant && variant.tagName === 'SELECT') {
      const option = variant.selectedOptions[0];
      const section = variant.closest('[data-product-section]');
      if (!option || !section) return;
      const currency = section.dataset.currency || 'USD';
      const locale = section.dataset.locale || document.documentElement.lang;
      const price = Number(option.dataset.price || 0);
      const compare = Number(option.dataset.compare || 0);
      const priceNode = section.querySelector('[data-product-price]');
      const buttonPrice = section.querySelector('[data-add-price]');
      const button = section.querySelector('[data-add-button]');
      const label = section.querySelector('[data-add-label]');
      const sku = section.querySelector('[data-product-sku]');
      if (priceNode) {
        const sale = compare > price ? ' <s>' + formatMoney(compare, currency, locale) + '</s>' : '';
        priceNode.innerHTML = formatMoney(price, currency, locale) + sale;
      }
      if (buttonPrice) buttonPrice.textContent = formatMoney(price, currency, locale);
      const available = option.dataset.available === 'true';
      if (button) button.disabled = !available;
      if (label) label.textContent = available ? (button.dataset.availableLabel || 'Add to bag') : 'Sold out';
      if (sku) sku.textContent = option.dataset.sku ? 'SKU ' + option.dataset.sku : '';
    }

    const builder = event.target.closest('[data-product-builder]');
    if (builder && event.target.matches('[data-base-choice], [data-addon-choice], [data-builder-variant]')) {
      updateBuilder(builder, event.target);
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form.product-form');
    if (!form) return;
    if (event.submitter?.name === 'checkout') return;
    event.preventDefault();
    const button = form.querySelector('[data-add-button]');
    const label = form.querySelector('[data-add-label]');
    const status = form.querySelector('[data-product-status]');
    if (!button || button.disabled) return;
    const original = label?.textContent || 'Add to bag';
    button.disabled = true;
    if (label) label.textContent = 'Adding…';
    if (status) status.textContent = '';
    try {
      const action = form.getAttribute('action') || ((window.Shopify?.routes?.root || '/') + 'cart/add');
      const url = action.endsWith('.js') ? action : action + '.js';
      const response = await fetch(url, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' }
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.description || result.message || 'Please try again.');
      await refreshCartCount();
      if (status) status.textContent = 'Added to your bag.';
      showToast('Added to your bag.');
      if (label) label.textContent = 'Added';
    } catch (error) {
      if (status) status.textContent = error.message;
      showToast(error.message);
      if (label) label.textContent = original;
    } finally {
      window.setTimeout(() => {
        button.disabled = false;
        if (label) label.textContent = original;
      }, 1200);
    }
  });

  document.addEventListener('click', async (event) => {
    const submit = event.target.closest('[data-builder-submit]');
    if (!submit) return;
    const builder = submit.closest('[data-product-builder]');
    if (!builder) return;
    const selectedBase = builder.querySelector('[data-base-choice]:checked');
    const selectedAddons = [...builder.querySelectorAll('[data-addon-choice]:checked')];
    const requiresBase = builder.dataset.requireBase === 'true';
    const message = builder.querySelector('[data-builder-message]');
    const max = Number(builder.dataset.maxAddons || 6);
    if ((requiresBase && !selectedBase) || (!selectedBase && selectedAddons.length === 0)) {
      if (message) message.textContent = requiresBase ? 'Choose a base piece to continue.' : 'Choose at least one piece to continue.';
      return;
    }
    if (selectedAddons.length > max) {
      if (message) message.textContent = 'Choose up to ' + max + ' add-ons.';
      return;
    }
    const selectedInputs = [selectedBase, ...selectedAddons].filter(Boolean);
    const items = selectedInputs.map((input) => {
      const card = input.closest('[data-builder-card]');
      return { id: Number(card?.querySelector('[data-builder-variant]')?.value), quantity: 1 };
    }).filter((item) => Number.isFinite(item.id) && item.id > 0);
    if (!items.length) {
      if (message) message.textContent = 'Choose an available option to continue.';
      return;
    }
    const original = submit.textContent;
    submit.disabled = true;
    submit.textContent = 'Adding…';
    if (message) message.textContent = '';
    try {
      await addItems(builder.dataset.addUrl, items);
      if (message) message.textContent = 'Your selection has been added to your bag.';
      showToast('Your selection has been added to your bag.');
      submit.textContent = 'Added to bag';
    } catch (error) {
      if (message) message.textContent = error.message;
      showToast(error.message);
    } finally {
      window.setTimeout(() => {
        submit.disabled = false;
        submit.textContent = original;
      }, 1600);
    }
  });

  const updateBuilder = (builder, changedInput = null) => {
    const currency = builder.dataset.currency || 'USD';
    const locale = builder.dataset.locale || document.documentElement.lang;
    const max = Number(builder.dataset.maxAddons || 6);
    const base = builder.querySelector('[data-base-choice]:checked');
    const addons = [...builder.querySelectorAll('[data-addon-choice]:checked')];
    const message = builder.querySelector('[data-builder-message]');
    if (addons.length > max) {
      const rejected = changedInput?.matches('[data-addon-choice]') && changedInput.checked ? changedInput : addons[addons.length - 1];
      rejected.checked = false;
      if (message) message.textContent = 'You can choose up to ' + max + ' add-ons.';
    } else if (message) {
      message.textContent = '';
    }
    builder.querySelectorAll('[data-builder-card]').forEach((card) => {
      const input = card.querySelector('[data-base-choice], [data-addon-choice]');
      card.classList.toggle('is-selected', Boolean(input?.checked));
      const variant = card.querySelector('[data-builder-variant]');
      const selectedOption = variant?.tagName === 'SELECT' ? variant.selectedOptions[0] : variant;
      const priceNode = card.querySelector('[data-builder-price]');
      if (priceNode && selectedOption?.dataset.price) {
        priceNode.textContent = formatMoney(selectedOption.dataset.price, currency, locale);
      }
    });
    const selected = [base, ...[...builder.querySelectorAll('[data-addon-choice]:checked')]].filter(Boolean);
    const total = selected.reduce((sum, input) => {
      const variant = input.closest('[data-builder-card]')?.querySelector('[data-builder-variant]');
      const option = variant?.tagName === 'SELECT' ? variant.selectedOptions[0] : variant;
      return sum + Number(option?.dataset.price || 0);
    }, 0);
    const totalNode = builder.querySelector('[data-builder-total]');
    if (totalNode) totalNode.textContent = formatMoney(total, currency, locale);
    const summary = builder.querySelector('[data-builder-summary]');
    if (summary) {
      const baseTitle = base?.closest('[data-builder-card]')?.querySelector('.builder-product__title')?.textContent;
      const count = builder.querySelectorAll('[data-addon-choice]:checked').length;
      const parts = [];
      if (baseTitle) parts.push(baseTitle);
      if (count) parts.push(count + ' add-on' + (count === 1 ? '' : 's'));
      summary.textContent = parts.length ? parts.join(' + ') : 'Select a base and any add-ons.';
    }
  };

  document.querySelectorAll('[data-product-builder]').forEach(updateBuilder);
})();
